import { FC, useState, useRef, useEffect } from 'react';
import { Control, FieldErrors, useWatch, UseFormSetValue } from 'react-hook-form';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Controller } from 'react-hook-form';
import { FormDropdown, FormInputText, FormInputNumber, FormCalendar } from '../../components/form';
import { SettingDataViewModel, FeeUpdateType } from '../../models/fees';
import { FieldMetadata, DataType, SpecialFieldType } from '../../models/expression';
import * as commonService from '../../services/commonService';
import styles from './SettingDataEditor.module.css';

interface SettingDataEditorProps {
  control: Control<any>;
  errors: FieldErrors;
  setValue: UseFormSetValue<any>;
  fieldMetadata: FieldMetadata[];
  ruleType?: string | null;
}

const TRUE_FALSE_OPTIONS = [
  { label: 'True', value: 'True' },
  { label: 'False', value: 'False' },
];

export const SettingDataEditor: FC<SettingDataEditorProps> = ({ control, errors, setValue, fieldMetadata, ruleType }) => {
  const settingData: SettingDataViewModel[] | null = useWatch({
    control,
    name: 'settingData',
  });
  const [enumValuesCache, setEnumValuesCache] = useState<Record<string, Array<{ label: string; value: string }>>>({});
  const [loadingEnums, setLoadingEnums] = useState<Record<string, boolean>>({});
  const fetchingEnumsRef = useRef<Set<string>>(new Set());
  
  const [losMetadataFlags, setLosMetadataFlags] = useState<Record<number, boolean>>(() => {
    if (settingData) {
      const flags: Record<number, boolean> = {};
      settingData.forEach((setting, index) => {
        flags[index] = setting.fieldId?.startsWith('LosMetadata.') || false;
      });
      return flags;
    }
    return {};
  });

  useEffect(() => {
    if (settingData) {
      setLosMetadataFlags((prevFlags) => {
        const newFlags: Record<number, boolean> = {};
        settingData.forEach((setting, index) => {
          newFlags[index] = prevFlags[index] !== undefined 
            ? prevFlags[index]
            : setting.fieldId?.startsWith('LosMetadata.') || false;
        });
        return newFlags;
      });
    }
  }, [settingData?.length]);

  useEffect(() => {
    if (ruleType !== 'Final' && settingData && settingData.length > 0) {
      const hasLosMetadata = settingData.some(setting => setting.fieldId?.startsWith('LosMetadata.'));
      if (hasLosMetadata) {
        const filteredData = settingData.filter(setting => !setting.fieldId?.startsWith('LosMetadata.'));
        setValue('settingData', filteredData.length > 0 ? filteredData : null, { shouldValidate: true });
      }
    }
  }, [ruleType]);

  const fieldOptions = fieldMetadata.map((fieldMetadata) => ({
    label: fieldMetadata.fieldName,
    value: fieldMetadata.fieldName,
  }));

  const getFieldMetadata = (fieldName: string | null): FieldMetadata | undefined => {
    if (!fieldName) return undefined;
    const cleanFieldName = fieldName.startsWith('LosMetadata.') 
      ? fieldName.substring('LosMetadata.'.length)
      : fieldName;
    return fieldMetadata.find((f) => f.fieldName === cleanFieldName);
  };

  const handleLosMetadataToggle = (index: number, checked: boolean) => {
    const currentData = settingData || [];
    const newData = [...currentData];
    const setting = newData[index];
    
    if (checked) {
      if (setting.fieldId && !setting.fieldId.startsWith('LosMetadata.')) {
        setting.fieldId = `LosMetadata.${setting.fieldId}`;
      } else if (!setting.fieldId) {
        setting.fieldId = 'LosMetadata.';
      }
    } else {
      if (setting.fieldId?.startsWith('LosMetadata.')) {
        setting.fieldId = setting.fieldId.substring('LosMetadata.'.length);
      }
    }
    
    setValue('settingData', newData, { shouldValidate: true });
    setLosMetadataFlags(prev => ({ ...prev, [index]: checked }));
  };

  const fetchEnumValues = async (enumTypeName: string) => {
    if (enumValuesCache[enumTypeName]) {
      return enumValuesCache[enumTypeName];
    }

    if (fetchingEnumsRef.current.has(enumTypeName)) {
      return [];
    }

    fetchingEnumsRef.current.add(enumTypeName);
    setLoadingEnums((prev) => ({ ...prev, [enumTypeName]: true }));
    
    try {
      const enumValues = await commonService.getEnumValues(enumTypeName);
      const options = enumValues.map((ev) => ({
        label: ev.displayName || ev.name || '',
        value: ev.value,
      }));
      setEnumValuesCache((prev) => ({ ...prev, [enumTypeName]: options }));
      return options;
    } catch (error) {
      console.error(`Failed to fetch enum values for ${enumTypeName}:`, error);
      return [];
    } finally {
      fetchingEnumsRef.current.delete(enumTypeName);
      setLoadingEnums((prev) => ({ ...prev, [enumTypeName]: false }));
    }
  };

  const getEnumValuesForField = (fieldId: string | null) => {
    const field = getFieldMetadata(fieldId);
    
    if (!field || !field.enumType) {
      return [];
    }

    if (enumValuesCache[field.enumType]) {
      return enumValuesCache[field.enumType];
    }

    if (!fetchingEnumsRef.current.has(field.enumType)) {
      fetchEnumValues(field.enumType);
    }

    return [];
  };

  const updateTypeOptions = Object.values(FeeUpdateType).map((type) => ({
    label: type,
    value: type,
  }));

  const handleAddSetting = () => {
    const currentData = settingData || [];
    const newData = [...currentData, { fieldId: null, value: null, updateType: FeeUpdateType.SetValue }];
    setValue('settingData', newData, { shouldValidate: true });
  };

  const handleRemoveSetting = (index: number) => {
    const currentData = settingData || [];
    const newData = currentData.filter((_, i) => i !== index);
    setValue('settingData', newData.length > 0 ? newData : null, { shouldValidate: true });
  };

  const handleFieldChange = (index: number, fieldId: string | null) => {
    const currentData = settingData || [];
    const newData = [...currentData];
    const field = fieldMetadata.find((f) => f.fieldName === fieldId);
    // Reset value to null for all field types (list fields now use single text input)
    const resetValue = null;
    const isListOrServiceProvider = field?.isList || field?.specialField === SpecialFieldType.ServiceProviderSelection;
    const defaultUpdateType = isListOrServiceProvider ? FeeUpdateType.AddValue : undefined;
    newData[index] = { ...newData[index], fieldId, value: resetValue, updateType: defaultUpdateType };
    setValue('settingData', newData, { shouldValidate: true });
  };

  const handleValueChange = (index: number, value: string | null) => {
    const currentData = settingData || [];
    const newData = [...currentData];
    newData[index] = { ...newData[index], value };
    setValue('settingData', newData, { shouldValidate: true });
  };

  const getFieldDataType = (fieldId: string | null): DataType | null => {
    const field = getFieldMetadata(fieldId);
    return field?.dataType || null;
  };

  const getValueOptions = (fieldId: string | null) => {
    const field = getFieldMetadata(fieldId);
    
    if (field?.dataType === DataType.TrueFalse) {
      return TRUE_FALSE_OPTIONS;
    }
    
    if (field?.dataType === DataType.Enumeration && field.enumType) {
      const enumValues = getEnumValuesForField(fieldId);
      return enumValues;
    }
    
    return [];
  };

  const isLoadingEnumForField = (fieldId: string | null): boolean => {
    const field = getFieldMetadata(fieldId);
    if (field?.dataType === DataType.Enumeration && field.enumType) {
      return loadingEnums[field.enumType] || false;
    }
    return false;
  };

  const shouldShowDropdown = (fieldId: string | null): boolean => {
    const dataType = getFieldDataType(fieldId);
    return dataType === DataType.TrueFalse || dataType === DataType.Enumeration;
  };

  const isFeeAmountField = (fieldId: string | null): boolean => {
    return fieldId?.toLowerCase().includes('feeamount') || false;
  };

  const isPercentageField = (fieldId: string | null): boolean => {
    return fieldId?.toLowerCase().includes('percentage') || false;
  };

  const isListField = (fieldId: string | null): boolean => {
    const field = getFieldMetadata(fieldId);
    return field?.isList || false;
  };

  const isServiceProviderField = (fieldId: string | null): boolean => {
    const field = getFieldMetadata(fieldId);
    return field?.specialField === SpecialFieldType.ServiceProviderSelection;
  };

  const handleServiceProviderChange = (index: number, serviceType: string | null, providerId: string | null) => {
    const currentData = settingData || [];
    const newData = [...currentData];
    const jsonValue = JSON.stringify({ serviceType: serviceType || '', serviceProviderId: providerId || '' });
    newData[index] = { ...newData[index], value: jsonValue };
    setValue('settingData', newData, { shouldValidate: true });
  };

  const parseServiceProviderValue = (value: string | null): { serviceType: string; serviceProviderId: string } => {
    if (!value) return { serviceType: '', serviceProviderId: '' };
    try {
      const parsed = JSON.parse(value);
      return { serviceType: parsed.serviceType || '', serviceProviderId: parsed.serviceProviderId || '' };
    } catch {
      return { serviceType: '', serviceProviderId: '' };
    }
  };


  return (
    <div className="flex flex-column gap-3">
      <label className="font-medium">
        Fee<span className="text-red-500">*</span>
      </label>

      {(!settingData || settingData.length === 0) && (
        <div className="text-sm text-red-600">
          <i className="pi pi-exclamation-triangle mr-1"></i>
          Required: At least one complete fee setting (Field ID and Value). Click "Add Setting" to begin.
        </div>
      )}

      {settingData && settingData.length > 0 && (
        <div className="flex flex-column gap-2">
          {settingData.map((setting, index) => (
            <div key={index} className={`p-fluid p-input-filled flex gap-2 text-sm w-full ${styles.row}`}>
              <div className="min-w-42 flex-1" style={{ maxWidth: '300px' }}>
                {losMetadataFlags[index] ? (
                  <Controller
                    name={`settingData.${index}.fieldId` as any}
                    control={control}
                    render={({ field }) => (
                      <div className="p-inputgroup">
                        <span className="p-inputgroup-addon">LosMetadata.</span>
                        <InputText
                          value={field.value?.startsWith('LosMetadata.') 
                            ? field.value.substring('LosMetadata.'.length) 
                            : field.value || ''}
                          onChange={(e) => {
                            const newValue = e.target.value ? `LosMetadata.${e.target.value}` : 'LosMetadata.';
                            field.onChange(newValue);
                          }}
                          placeholder="Enter field ID"
                          className="w-full"
                        />
                      </div>
                    )}
                  />
                ) : (
                  <FormDropdown
                    control={control}
                    errors={errors}
                    field={`settingData.${index}.fieldId`}
                    label=""
                    options={fieldOptions}
                    optionLabel="label"
                    optionValue="value"
                    placeholder="Select field"
                    className="mb-0"
                    onChange={(e) => handleFieldChange(index, e.value)}
                    showFilter
                  />
                )}
              </div>

              <div className="min-w-42 flex-1" style={{ maxWidth: '300px' }}>
                {losMetadataFlags[index] ? (
                  <FormInputText
                    control={control}
                    errors={errors}
                    field={`settingData.${index}.value`}
                    label=""
                    placeholder="Enter value"
                    className="mb-0"
                    onChange={(e) => handleValueChange(index, e.target.value)}
                  />
                ) : isServiceProviderField(setting.fieldId) ? (
                  <div className="flex gap-2">
                    <Dropdown
                      value={parseServiceProviderValue(setting.value).serviceType}
                      options={getEnumValuesForField(setting.fieldId)}
                      optionLabel="label"
                      optionValue="value"
                      placeholder="Service Type *"
                      className="flex-1"
                      onChange={(e) => {
                        const parsed = parseServiceProviderValue(setting.value);
                        handleServiceProviderChange(index, e.value, parsed.serviceProviderId);
                      }}
                      filter
                      appendTo="self"
                    />
                    {setting.updateType !== FeeUpdateType.RemoveValue && (
                      <InputText
                        value={parseServiceProviderValue(setting.value).serviceProviderId}
                        onChange={(e) => {
                          const parsed = parseServiceProviderValue(setting.value);
                          handleServiceProviderChange(index, parsed.serviceType, e.target.value);
                        }}
                        placeholder="Provider ID *"
                        className="flex-1"
                      />
                    )}
                  </div>
                ) : isListField(setting.fieldId) ? (
                  <FormInputText
                    control={control}
                    errors={errors}
                    field={`settingData.${index}.value`}
                    label=""
                    placeholder="Enter value"
                    className="mb-0"
                    onChange={(e) => handleValueChange(index, e.target.value)}
                  />
                ) : shouldShowDropdown(setting.fieldId) ? (
                  <FormDropdown
                    control={control}
                    errors={errors}
                    field={`settingData.${index}.value`}
                    label=""
                    options={getValueOptions(setting.fieldId)}
                    optionLabel="label"
                    optionValue="value"
                    placeholder={isLoadingEnumForField(setting.fieldId) ? 'Loading...' : 'Select value'}
                    className="mb-0"
                    onChange={(e) => handleValueChange(index, e.value)}
                    disabled={isLoadingEnumForField(setting.fieldId)}
                    showFilter
                  />
                ) : getFieldDataType(setting.fieldId) === DataType.Number || 
                   getFieldDataType(setting.fieldId) === DataType.Integer ? (
                  <FormInputNumber
                    control={control}
                    errors={errors}
                    field={`settingData.${index}.value`}
                    label=""
                    placeholder="Enter value"
                    className="mb-0"
                    prefix={isFeeAmountField(setting.fieldId) ? '$ ' : undefined}
                    suffix={isPercentageField(setting.fieldId) ? ' %' : undefined}
                  />
                ) : getFieldDataType(setting.fieldId) === DataType.Date ||
                   getFieldDataType(setting.fieldId) === DataType.DateTime ? (
                  <FormCalendar
                    control={control}
                    errors={errors}
                    field={`settingData.${index}.value`}
                    label=""
                    placeholder="Select date"
                    className="mb-0"
                    showTime={getFieldDataType(setting.fieldId) === DataType.DateTime}
                  />
                ) : (
                  <FormInputText
                    control={control}
                    errors={errors}
                    field={`settingData.${index}.value`}
                    label=""
                    placeholder="Enter value"
                    className="mb-0"
                    onChange={(e) => handleValueChange(index, e.target.value)}
                  />
                )}
              </div>

              {ruleType === 'Final' && (
                <div className="flex align-items-center" style={{ minWidth: '120px' }}>
                  <Checkbox
                    inputId={`los-metadata-${index}`}
                    checked={losMetadataFlags[index] || false}
                    onChange={(e) => handleLosMetadataToggle(index, e.checked || false)}
                  />
                  <label 
                    htmlFor={`los-metadata-${index}`} 
                    className="ml-2 text-sm cursor-pointer"
                    style={{ userSelect: 'none' }}
                  >
                    LOS Metadata
                  </label>
                </div>
              )}

              <div className="min-w-32" style={{ maxWidth: '180px' }}>
                <FormDropdown
                  control={control}
                  errors={errors}
                  field={`settingData.${index}.updateType`}
                  label=""
                  options={isServiceProviderField(setting.fieldId) || isListField(setting.fieldId)
                    ? updateTypeOptions.filter(o => o.value !== FeeUpdateType.SetValue)
                    : updateTypeOptions}
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Update Type"
                  className="mb-0"
                />
              </div>

              <Button
                type="button"
                icon="pi pi-trash"
                className="p-button-text p-button-sm p-button-danger"
                onClick={() => handleRemoveSetting(index)}
                style={{ minWidth: '32px', padding: '0.25rem' }}
              />
            </div>
          ))}
        </div>
      )}

      <div>
        <Button
          type="button"
          label="Add Setting"
          icon="pi pi-plus"
          link
          size="small"
          onClick={handleAddSetting}
          className="font-bold mt-2"
        />
      </div>
    </div>
  );
};

